import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Leaf } from "lucide-react";

function getSessionId() {
  let sid = localStorage.getItem("sp_chat_sid");
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("sp_chat_sid", sid);
  }
  return sid;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! 👋 Curious if converting your LLC to an S-Corp could save you on taxes? Ask me anything — or tell me your situation and I'll recommend the right plan." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("scorppath:open-chat", openHandler);
    return () => window.removeEventListener("scorppath:open-chat", openHandler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit a snag. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open-btn"
          className="fixed bottom-24 right-6 z-50 h-14 w-14 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white shadow-xl flex items-center justify-center transition-all hover:scale-105"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-[#E5E5E5] flex flex-col overflow-hidden"
          data-testid="chat-window"
        >
          <div className="bg-[#114A31] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                <Leaf className="h-4 w-4" />
              </span>
              <div>
                <p className="font-heading font-semibold text-sm leading-tight">ScorpPath Assistant</p>
                <p className="text-[11px] text-white/70 leading-tight">Typically replies instantly</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9F9F8]" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#114A31] text-white rounded-br-sm"
                      : "bg-white border border-[#E5E5E5] text-[#1C1C1C] rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E5E5E5] rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-[#114A31]" />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#E5E5E5] bg-white flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about S-Corp savings…"
              data-testid="chat-input"
              className="flex-1 rounded-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:outline-none focus:border-[#114A31]"
            />
            <button
              onClick={send}
              disabled={loading}
              data-testid="chat-send-btn"
              className="h-10 w-10 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white flex items-center justify-center shrink-0 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
